import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import { Icon } from "@iconify/react";

import {
  FormControl,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  Tooltip,
} from "@mui/material";

import { DataGrid, gridClasses } from "@mui/x-data-grid";
import "./AgentMonitoring.css";
import { SeverityPill } from "../Dashboard/SeverityPill";
import { agentState, changeAgentStatus } from "../Store/Actions/AuthAction";
import { getAgentMonitoring } from "../Store/Actions/MonitoringAction";
import { SET_SELECTED_AGENT_ID } from "../Store/Types/AgentTypes";

const AgentMonitoring = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const dispatch = useDispatch();
  const { myInfo } = useSelector((state) => state.auth);

  useEffect(() => {
    const fetchData = () => {
      getAgentMonitoring(myInfo.user.AgentID)
        .then((response) => {
          if (response.status) {
            console.log("Agent monitor data: ", response.data);

            setData(
              response.data.filter(
                (dataToFilter) => dataToFilter.CurrentState !== "LOGGEDOFF"
              )
            );
          } else {
            console.log("error", response.error);
          }

          setIsLoading(false);
        })
        .catch((err) => {
          console.log("err", err);
          setIsLoading(false);
        });
    };

    // Fetch data on first render
    fetchData();

    // Get agent state options
    agentState()
      .then((response) => {
        if (response.status) {
          setStateOptions(response.data);
        } else {
          toast.warn(response.error);
        }
      })
      .catch((err) => {
        toast.warn(err);
      });

    // Fetch data every one second
    const interval = setInterval(() => {
      fetchData();
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const columns = [
    {
      field: "AgentCode",
      flex: 1,
      headerName: "Agent Code",
    },

    {
      field: "AgentName",
      flex: 1,
      headerName: "Agent Name",
    },

    {
      field: "Ext",
      flex: 1,
      headerName: "Ext",
      maxWidth: 60,
    },

    {
      field: "CurrentState",
      flex: 1,
      headerName: "State",
      maxWidth: 150,

      renderCell: (params) => (
        /* <SeverityPill color={params.row.CurrentState.toLowerCase()}>
          {params.row.CurrentState}{" "}
          {params.row.CurrentState === "AUX" &&
            ` - ${params.row.CurrentStateReason}`}
        </SeverityPill> */

        <FormControl
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              "& > fieldset": {
                border: "none",
              },
            },
          }}
          className={`agent_status_color ${renderAgentStateColor(
            params.row.CurrentState
          )}`}
        >
          <Select
            value={
              params.row.CurrentStateReason
                ? `${params.row.CurrentState} - ${params.row.CurrentStateReason}`
                : params.row.CurrentState
            }
            onChange={(event) => {
              changeAgentStatus({
                agentcode: params.row.AgentCode,
                reasoncode: event.target.value,
              })
                .then((response) => {
                  if (response.status) {
                    toast.success("Agent status changed.", { autoClose: 3000 });
                  } else {
                    console.log("error", response.error);

                    toast.error("Error changing agent status.", {
                      autoClose: 3000,
                    });
                  }
                })
                .catch((err) => {
                  console.log("err", err);

                  toast.error("Error changing agent status.", {
                    autoClose: 3000,
                  });
                });
            }}
            input={
              <OutlinedInput sx={{ fontSize: "14px", fontWeight: "600" }} />
            }
            displayEmpty
            className="SelectAgentStatus"
            disabled={
              params.row.CurrentState !== "READY" &&
              params.row.CurrentState !== "AUX"
            }
          >
            {stateOptions.map((x) => {
              return (
                <MenuItem
                  sx={{
                    fontSize: "14px",
                    padding: "6px 12px",
                    justifyContent: "center",
                    display: x.isAllow ? "flex" : "none",
                  }}
                  value={x.ReasonCode}
                  key={x.ReasonCode}
                >
                  {x.Description}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      ),
    },

    {
      field: "StateDuration",
      flex: 1,
      headerName: "State Duration",
      maxWidth: 130,
    },

    {
      field: "LoginTime",
      flex: 1,
      headerName: "Login Time",
      renderCell: (params) =>
        moment(params.row.LoginTime).format("DD-MMM-yyyy HH:mm:ss"),
    },

    {
      flex: 0.5,
      headerName: "Action",

      renderCell: (params) => {
        // Hide button for this agent and logged off agents
        if (
          params.row.AgentID === myInfo.user.AgentID ||
          params.row.CurrentState === "LOGGEDOFF"
        ) {
          return;
        } else {
          return (
            <Tooltip arrow placement="top" title="Message agent">
              <IconButton
                onClick={() => {
                  dispatch({
                    type: SET_SELECTED_AGENT_ID,
                    payload: params.row.AgentID,
                  });
                }}
              >
                <Icon height="26" icon="ion:chatbox-ellipses-outline" />
              </IconButton>
            </Tooltip>
          );
        }
      },
    },
  ];

  function renderAgentStateColor(state) {
    switch (state) {
      case "READY":
        return "BGcolor1";

      case "AUX":
        return "BGcolor2";

      case "OFFLINE":
        return "BGcolor3";

      case "BUSY":
        return "BGcolor4";

      case "ACW":
        return "BGcolor5";

      case "NOT BOUND":
        return "BGcolor3";
    
      default:
        break;
    }
  }

  return (
    <DataGrid
      autoPageSize
      className="agent-monitoring table"
      columns={columns}
      disableColumnFilter
      disableColumnMenu
      disableSelectionOnClick
      loading={isLoading}
      rows={data}
      sx={{
        [`& .${gridClasses.cell}`]: {
          py: 1.8,
        },
      }}
      getRowClassName={() => "row"}
      getRowId={(row) => row.AgentID}
    />
  );
};

export default AgentMonitoring;
